import { useSearchParams } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { eventTypes } from "../constants/eventTypes";
import { sortByDateTime } from "../hooks/DateTime";
import { useAuth } from "../contexts/AuthContext";
import { ProviderData } from "../types/firebase";
import { useState, useEffect } from "react";

import FeaturedCard from "../components/FeaturedCard";
import Pill from "../components/Pill";
import ExternalLinks from "../components/ExternalLinks";

export default function SinglePractioner() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { providerData, eventsData, loading, error, followPractitioner, unfollowPractitioner, isFollowing } = useData();
  const { currentUser } = useAuth();
  const [isUserFollowing, setIsUserFollowing] = useState(false);

  const practioner = providerData.find((provider) => provider.id === id);
  const practitionerEvents = practioner
    ? sortByDateTime(eventsData.filter(
        (event) => event.provider_name === practioner.provider_name
      ))
    : [];

  useEffect(() => {
    const checkFollowStatus = async () => {
      if (currentUser && practioner) {
        const following = await isFollowing(practioner.id, currentUser.uid);
        setIsUserFollowing(following);
      }
    };
    checkFollowStatus();
  }, [currentUser, practioner]);

  const userCanEdit = (practioner: ProviderData) => {
    if (!currentUser) return false;
    return currentUser.admin || currentUser.uid === practioner.owner_id;
  };

  const handleFollowToggle = async () => {
    if (!currentUser || !practioner) return;
    try {
      if (isUserFollowing) {
        await unfollowPractitioner(practioner.id, currentUser.uid);
        setIsUserFollowing(false);
      } else {
        await followPractitioner(practioner.id, currentUser.uid);
        setIsUserFollowing(true);
      }
    } catch (error) {
      console.error("Error toggling follow status:", error);
      alert("Unable to update follow status. Please try again later.");
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!practioner) return <div>Practioner not found</div>;

  return (
    <div className="container mx-auto px-4 py-8">
        <Pill 
        text={"Back"}
        colorClass="bg-white" 
        textColor="muted-grey" 
        icon="arrow" 
        clickable={true}
        path={"/practitioners"}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="practioner text-center flex flex-col items-center">
         
            {practioner.provider_logo && (
              <img
                className="rounded-full m-4 w-24 h-24 border-2 border-grey"
                src={practioner.provider_logo}
                alt={`${practioner.provider_name} logo`}
              />
            )}
          <h1 className="text-2xl font-bold mt-4 mb-4">
            {practioner.provider_name}
          </h1>
          <p className="w-[20rem]">{practioner.provider_description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-gray-600">
              {practioner.followers_count || 0} followers
            </span>
            {currentUser && currentUser.uid !== practioner.owner_id && (
              <button 
                onClick={handleFollowToggle}
                className="ml-2 px-4 py-1 text-sm font-medium text-white bg-muted-lavender hover:bg-muted-darkGrey transition-colors rounded-full"
              >
                {isUserFollowing ? 'Unfollow' : 'Follow'}
              </button>
            )}
          </div>
          <div className="w-[20rem] flex flex-wrap justify-center mt-4 mb-4">
            {practioner.provider_types?.map((type) => {
              const eventType = eventTypes.find(et => et.type === type);
              return eventType ? (
                <Pill
                  key={type}
                  text={eventType.label}
                  colorClass="bg-muted-grey"
                  size="small"
                  extraClass="m-1"
                />
              ) : null;
            })}
          </div>
          {practioner.provider_country && practioner.provider_region && (
            <p>{practioner.provider_region}, {practioner.provider_country}</p>
          )}
          <div className="m-4">

            <ExternalLinks tiktok={practioner.providers_tiktok} instagram={practioner.provider_instagram} website={practioner.provider_link}/>
          </div>
        </div>
        <div className="events">
          <h2 className="text-2xl font-bold mt-4 mb-4">Events</h2>
          {practitionerEvents.length > 0 ? (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
              {practitionerEvents.map((event) => (
                <FeaturedCard 
                  key={event.id} 
                  event={event} 
                  location="practitioners"
                  userCanEdit={practioner ? userCanEdit(practioner) : false}
                />
              ))}
            </div>
          ) : (
            <p>No events listed yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
